// @givmo/app-admin — Settings → Payment Methods company-wallet decode/mapping + deposit amount rules
// (EP-WEB6). Pure, side-effect-free helpers so the field mapping + the money-entry validation are
// unit-testable in isolation (matching the `payment-methods-decode.ts` / donate `amount.ts` split).
//
// Two concerns live here:
//   1. WALLET VIEW — map the `WalletOut` wire model (balance + the additive PV0-BE1 held/available
//      fields) to the display shape the balance card renders. Money is NEVER parsed to a number: the
//      wire value is a decimal string and is only FORMATTED (via `formatMoneyDisplay`), never
//      arithmetic'd — the api-client money law. Tolerant reader: a missing/undefined wallet or an
//      unformattable balance degrades gracefully, never throws.
//   2. DEPOSIT AMOUNT — integer-dollar validation matching the shipped clients. The backend enforces
//      only "net amount > 0" (`NegativeAmountException`); the shipped iOS `CompanyDepositScreen`
//      presents whole dollars, min $1 / max $50,000, presets $20/$50/$200 (default $20). We mirror
//      that client-side envelope so the UX matches the native deposit screen and a fat-fingered
//      amount is caught before any charge. Validation returns a CODED reason so the UI reacts without
//      ever logging the amount value itself (PII-safe posture, parity with donate `amount.ts`).

import { formatMoneyDisplay, type WalletOut } from '@givmo/api-client';

// ── Wallet view ────────────────────────────────────────────────────────────────────────────────

/** The company wallet reduced to exactly the strings the balance card renders. */
export interface WalletView {
  /** Formatted balance with a leading `$` (e.g. "$1,250.00"). Always present (falls back to raw). */
  balanceDisplay: string;
  /** Formatted available-to-spend (balance − active holds) with `$`, or null when not computed. */
  availableDisplay: string | null;
  /** Formatted held amount with `$`, or null when absent/zero. */
  heldDisplay: string | null;
  /** True when there is a non-zero active hold — the available/held secondary line is shown. */
  hasHold: boolean;
}

/**
 * Map a `WalletOut` (or undefined, while the query is pending/errored) to the display view. Tolerant:
 * an absent wallet yields a "$0.00" placeholder rather than throwing, and an unformattable balance
 * falls back to the raw wire string (tolerant-reader law — never guess about money).
 */
export function toWalletView(wallet: WalletOut | undefined): WalletView {
  const balanceRaw = typeof wallet?.balance === 'string' ? wallet.balance : null;
  const heldRaw = typeof wallet?.held_amount === 'string' ? wallet.held_amount : null;
  const availableRaw =
    typeof wallet?.available_balance === 'string' ? wallet.available_balance : null;

  const hasHold = heldRaw !== null && isNonZeroMoney(heldRaw);

  return {
    balanceDisplay: formatMoneyWithSymbol(balanceRaw) ?? '$0.00',
    // Only surface the available/held pair when there is an actual hold — otherwise available == balance
    // and the extra line is noise.
    availableDisplay: hasHold ? formatMoneyWithSymbol(availableRaw) : null,
    heldDisplay: hasHold ? formatMoneyWithSymbol(heldRaw) : null,
    hasHold,
  };
}

/** Format a decimal money string as "$1,234.56", or null when it is not a plain decimal / is absent. */
export function formatMoneyWithSymbol(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const formatted = formatMoneyDisplay(value);
  if (formatted !== null) return `$${formatted}`;
  // Tolerant fallback: render a non-empty raw string verbatim rather than dropping it.
  const trimmed = value.trim();
  return trimmed.length > 0 ? `$${trimmed}` : null;
}

/** Whether a decimal money string represents a strictly-positive amount (not "0", "0.00", "", etc.). */
export function isNonZeroMoney(value: string): boolean {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return false;
  if (match[1] === '-') return false; // a negative hold is nonsensical; treat as no hold.
  const digits = `${match[2] ?? ''}${match[3] ?? ''}`;
  return /[1-9]/.test(digits);
}

// ── Deposit amount rules (integer dollars — mirrors the shipped iOS CompanyDepositScreen) ────────

export const MIN_DEPOSIT_DOLLARS = 1;
export const MAX_DEPOSIT_DOLLARS = 50_000;

/** Preset chips offered in the deposit dialog (whole dollars), matching the iOS deposit screen. */
export const DEPOSIT_PRESETS: readonly number[] = [20, 50, 200];

/** The default selected deposit amount (whole dollars), matching the iOS deposit screen. */
export const DEFAULT_DEPOSIT_DOLLARS = 20;

/** Why an entered deposit amount is invalid (coded — never the value itself). */
export type DepositAmountError = 'empty' | 'not-integer' | 'below-min' | 'above-max';

export interface DepositAmountValidation {
  valid: boolean;
  error: DepositAmountError | null;
}

/** The integer-dollar pattern: digits only, no decimal, no sign. */
const INTEGER_DOLLARS = /^\d+$/;

/**
 * Validate a raw integer-dollar deposit string. Whitespace and thousands-commas are stripped first; a
 * leading-zero value like "01" is rejected as non-integer to keep the amount canonical.
 */
export function validateDepositAmount(raw: string): DepositAmountValidation {
  const value = raw.trim().replace(/,/g, '');
  if (value.length === 0) return { valid: false, error: 'empty' };
  if (!INTEGER_DOLLARS.test(value) || (value.length > 1 && value.startsWith('0'))) {
    return { valid: false, error: 'not-integer' };
  }
  const dollars = Number(value);
  if (dollars < MIN_DEPOSIT_DOLLARS) return { valid: false, error: 'below-min' };
  if (dollars > MAX_DEPOSIT_DOLLARS) return { valid: false, error: 'above-max' };
  return { valid: true, error: null };
}

/** Human-readable message for a deposit-amount error (no value echoed). */
export function depositAmountErrorMessage(error: DepositAmountError): string {
  switch (error) {
    case 'empty':
      return 'Enter a deposit amount.';
    case 'not-integer':
      return 'Enter a whole dollar amount.';
    case 'below-min':
      return `The minimum deposit is $${MIN_DEPOSIT_DOLLARS}.`;
    case 'above-max':
      return `The maximum deposit is $${MAX_DEPOSIT_DOLLARS.toLocaleString('en-US')}.`;
  }
}

/**
 * The whole-dollar integer for a VALID amount string (commas stripped), or null when invalid. Used for
 * the button/summary label and the idempotency signature — callers must `validateDepositAmount` first.
 */
export function depositDollars(raw: string): number | null {
  const validation = validateDepositAmount(raw);
  if (!validation.valid) return null;
  return Number(raw.trim().replace(/,/g, ''));
}

/**
 * Convert a validated integer-dollar string to the wire cents-precision decimal string, e.g.
 * "50" → "50.00". Returns null when the amount is invalid (callers must validate first). Money law:
 * built by STRING concatenation, never `n / 100` or a float round-trip.
 */
export function toDepositWireAmount(raw: string): string | null {
  const dollars = depositDollars(raw);
  if (dollars === null) return null;
  return `${dollars}.00`;
}

/**
 * The amount field's text for a wire amount the dialog sent, e.g. "50.00" → "50" — the inverse of
 * {@link toDepositWireAmount}, for a reopened dialog that starts from a held deposit (CAM-R51). `null`
 * for anything `toDepositWireAmount` would not have produced. By string, never a float round-trip.
 */
export function depositEntryFromWire(wire: string): string | null {
  const match = /^(\d+)\.00$/.exec(wire);
  const entry = match?.[1] ?? null;
  return entry !== null && toDepositWireAmount(entry) === wire ? entry : null;
}

/** Format a whole-dollar integer as "$1,234" (no cents) — the deposit button/summary label. */
export function formatDollars(dollars: number): string {
  return `$${dollars.toLocaleString('en-US')}`;
}

// ── Cover processing fees (CAM-BE11, D-CAM-37) ──────────────────────────────────────────────────
//
// A company deposit may cover the card processing fee on top, so the wallet receives exactly the
// amount entered. The dialog states the charge BEFORE charging, so it states the SERVER's price:
// `GET /admin/company/wallet/deposit/quote` (`deposit-quote-transport.ts`) runs the resolution the
// charge itself uses — the stored card's brand row (which `GET /fees/processor` withholds by design),
// the company's own cover-fees setting and the deposit's platform-fee waiver — and answers both
// requests the dialog can make. There is no fee arithmetic here (CAM-BE11 review F1). The deposit then
// sends the gross it stated (`expected_amount_charged`); the backend refuses any other gross with
// `DEPOSIT_QUOTE_CHANGED` before money moves.

/** What one deposit request would charge and credit — money as the exact decimal strings served. */
export interface DepositQuoteBreakdown {
  /** The gross the card is charged. */
  readonly amountCharged: string;
  /** The processing fee inside `amountCharged`. */
  readonly processorFee: string;
  /** What the company wallet receives. */
  readonly amountCredited: string;
  /** Whether this request is booked as covering the fee. */
  readonly coverFees: boolean;
}

/** The server's quote for one (amount, card): both requests the dialog can make. */
export interface DepositQuote {
  /** The company's own setting covers every deposit; both breakdowns are then covered and equal. */
  readonly companyCoversFees: boolean;
  /** A deposit sent with `cover_fees: false`. */
  readonly uncovered: DepositQuoteBreakdown;
  /** A deposit sent with `cover_fees: true`. */
  readonly covered: DepositQuoteBreakdown;
}

const PLAIN_DECIMAL = /^\d+(?:\.\d+)?$/;

function decimalField(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  return typeof value === 'string' && PLAIN_DECIMAL.test(value) ? value : null;
}

function toBreakdown(raw: unknown): DepositQuoteBreakdown | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const amountCharged = decimalField(row, 'amount_charged');
  const processorFee = decimalField(row, 'processor_fee');
  const amountCredited = decimalField(row, 'amount_credited');
  if (amountCharged === null || processorFee === null || amountCredited === null) return null;
  if (typeof row.cover_fees !== 'boolean') return null;
  return { amountCharged, processorFee, amountCredited, coverFees: row.cover_fees };
}

/**
 * Decode `GET /admin/company/wallet/deposit/quote`, FAILING CLOSED. Every figure lands in a
 * confirm-before-charge sentence and in the `expected_amount_charged` the charge is held to, so a
 * missing, non-decimal or mistyped field makes the whole quote unusable (`null`): the dialog then
 * treats it as a failed quote and holds Deposit, rather than stating a guessed figure.
 */
export function toDepositQuote(body: unknown): DepositQuote | null {
  if (typeof body !== 'object' || body === null) return null;
  const quote = body as Record<string, unknown>;
  if (typeof quote.company_covers_fees !== 'boolean') return null;
  const uncovered = toBreakdown(quote.uncovered);
  const covered = toBreakdown(quote.covered);
  if (uncovered === null || covered === null) return null;
  return { companyCoversFees: quote.company_covers_fees, uncovered, covered };
}

/**
 * A charge figure for the confirm sentence: whole dollars without cents ("$20", as the uncovered
 * confirm has always read), anything else to the cent ("$20.87"). Exact string formatting.
 */
export function formatChargeFigure(value: string): string {
  const whole = /^(\d+)(?:\.0+)?$/.exec(value);
  if (whole !== null) return formatDollars(Number(whole[1]));
  return formatMoneyWithSymbol(value) ?? `$${value}`;
}

/** A fee figure, always to the cent ("$0.84"). The row's words are `components/cover-fees-copy.ts`. */
export function formatFeeFigure(value: string): string {
  return formatMoneyWithSymbol(value) ?? `$${value}`;
}
